import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertDialog } from "@astryxdesign/core/AlertDialog";
import { Badge } from "@astryxdesign/core/Badge";
import { Banner } from "@astryxdesign/core/Banner";
import { Button } from "@astryxdesign/core/Button";
import { Divider } from "@astryxdesign/core/Divider";
import { MetadataList, MetadataListItem } from "@astryxdesign/core/MetadataList";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/Stack";
import { Screen } from "@/components/layout/Screen";
import { ErrorState, LoadingRows } from "@/components/states";
import { useStudio } from "@/features/studio/StudioRoot";
import { publicApi } from "@/lib/api/public";
import { formatDateTime, formatDuration, formatMoney } from "@/lib/format";
import { myBookings } from "@/lib/storage";
import { errorMessage } from "@/lib/errors";
import { NotifyAndCalendar } from "./NotifyAndCalendar";
import { RescheduleSheet } from "./RescheduleSheet";
import { useT } from "@/lib/i18n";

const STATUS_VARIANT: Record<string, "success" | "neutral" | "error" | "warning"> = {
  confirmed: "success",
  completed: "neutral",
  cancelled: "error",
  no_show: "warning",
};

/** Token from the URL fragment: never sent to the static host or logged in its access logs. */
function useTokenFromHash(): string | null {
  const { hash } = useLocation();
  const m = /(?:^#|&)t=(bk_[A-Za-z0-9_-]{43})/.exec(hash);
  return m?.[1] ?? null;
}

export function BookingScreen() {
  const studio = useStudio();
  const t = useT();
  const token = useTokenFromHash();
  const location = useLocation();
  const justBooked = (location.state as { justBooked?: boolean } | null)?.justBooked === true;
  const qc = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [copied, setCopied] = useState(false);

  const booking = useQuery({
    queryKey: ["booking", token],
    queryFn: () => publicApi.booking(token!),
    enabled: !!token,
  });

  useEffect(() => {
    if (booking.data && token) {
      myBookings.save(studio.slug, { token, startsAt: booking.data.starts_at, serviceName: booking.data.service_name, savedAt: new Date().toISOString() });
    }
  }, [booking.data, token, studio.slug]);

  const cancel = useMutation({
    mutationFn: () => publicApi.cancel(token!),
    onSuccess: (b) => {
      qc.setQueryData(["booking", token], b);
      void qc.invalidateQueries({ queryKey: ["slots", studio.slug] });
      setConfirmCancel(false);
    },
  });

  if (!token) {
    return (
      <Screen title={t.bookingPage.title} backTo={`/s/${studio.slug}/my`}>
        <Banner status="warning" title={t.bookingPage.incompleteTitle} description={t.bookingPage.incompleteText} collapsible={false} />
      </Screen>
    );
  }

  const b = booking.data;
  return (
    <Screen title={justBooked ? t.bookingPage.booked : t.bookingPage.title} backTo={`/s/${studio.slug}/my`}>
      {booking.isPending ? (
        <LoadingRows rows={5} height={40} />
      ) : booking.isError ? (
        <ErrorState error={booking.error} onRetry={() => void booking.refetch()} />
      ) : b ? (
        <VStack gap={4}>
          {justBooked ? (
            <Banner
              status="success"
              title={t.bookingPage.confirmedTitle}
              description={t.bookingPage.confirmedText}
              collapsible={false}
            />
          ) : null}
          <VStack gap={2}>
            <HStack>
              <Badge variant={STATUS_VARIANT[b.status] ?? "neutral"} label={t.bookingPage.status[b.status] ?? b.status} />
            </HStack>
            <MetadataList>
              <MetadataListItem label={t.booking.service}>{b.service_name}</MetadataListItem>
              <MetadataListItem label={t.booking.when}>{formatDateTime(b.starts_at, b.tenant.timezone)}</MetadataListItem>
              <MetadataListItem label={t.booking.duration}>{formatDuration(b.duration_minutes)}</MetadataListItem>
              <MetadataListItem label={studio.config.resource_label.one}>{b.resource_name}</MetadataListItem>
              <MetadataListItem label={t.booking.price}>
                {b.price_amount > 0 ? formatMoney(b.price_amount, b.currency) : t.price.byStudio}
              </MetadataListItem>
              {b.tenant.address ? <MetadataListItem label={t.bookingPage.address}>{b.tenant.address}</MetadataListItem> : null}
            </MetadataList>
          </VStack>

          <Button
            label={copied ? t.bookingPage.copied : t.bookingPage.copy}
            variant="ghost"
            width="100%"
            onClick={() => {
              void navigator.clipboard?.writeText(window.location.href).then(() => setCopied(true), () => setCopied(false));
            }}
          />

          {b.status === "confirmed" ? (
            <>
              <Divider />
              <NotifyAndCalendar booking={b} token={token} slug={studio.slug} />
              <Divider />
              {b.can_change ? (
                <VStack gap={2}>
                  <Button label={t.bookingPage.reschedule} variant="secondary" width="100%" onClick={() => setRescheduling(true)} />
                  <Button label={t.bookingPage.cancel} variant="destructive" width="100%" onClick={() => setConfirmCancel(true)} />
                </VStack>
              ) : (
                <Text type="supporting">{t.bookingPage.cantChange(Math.round(studio.rules.cancel_cutoff_minutes / 60), studio.config.contacts.phone)}</Text>
              )}
            </>
          ) : null}
          {cancel.isError ? <Banner status="error" title={t.bookingPage.cancelFailed} description={errorMessage(cancel.error)} collapsible={false} /> : null}

          <AlertDialog
            isOpen={confirmCancel}
            onOpenChange={setConfirmCancel}
            title={t.bookingPage.cancelQuestion}
            description={t.bookingPage.cancelText(`${b.service_name}, ${formatDateTime(b.starts_at, b.tenant.timezone)}`)}
            actionLabel={t.bookingPage.cancel}
            cancelLabel={t.bookingPage.keep}
            isActionLoading={cancel.isPending}
            onAction={() => cancel.mutate()}
          />
          {rescheduling ? (
            <RescheduleSheet
              booking={b}
              token={token}
              onClose={() => setRescheduling(false)}
              onDone={(updated) => {
                qc.setQueryData(["booking", token], updated);
                setRescheduling(false);
              }}
            />
          ) : null}
        </VStack>
      ) : null}
    </Screen>
  );
}
